/**
 * Canonical live-trading hotkey registry (#725).
 *
 * One typed contract shared by the key listener (`Lt2HotkeyListener`), the
 * Settings remap UI, and every surface that TEACHES or PREVIEWS a key — the
 * ⌨ focus chip, the header target readout, the Attack arm tooltip, and the
 * order receipts. Before this file each of those spelled "A/D" and "W/E" out
 * by hand, which is exactly what made the keys unchangeable: a remap would
 * have left six surfaces quietly lying about what the trader's keyboard does.
 *
 * Modelled on `lib/alerts/registry.ts` (#231): every action the hotkey layer
 * exposes has exactly one entry here with a STABLE id, and new optional
 * metadata is ADDED to the entry rather than introduced as a parallel list.
 *
 * ### The id is the action, never the key
 *
 * `LiveHotkeyActionId` names what the action DOES. The key is a mutable
 * property of the action, so remapping changes one field and nothing else —
 * in particular it must never change:
 *
 *   • the `Lt2InputMethod` audit value stamped into the durable `__lt2`
 *     record (`hotkey-a` keeps meaning "the passive top/left action" for
 *     every order ever placed, whatever key fired it);
 *   • the `slot` the fence proves visibility against;
 *   • any grant, ceiling, arming state, or entitlement.
 *
 * A remap is INPUT ROUTING ONLY. There is deliberately no path from this
 * module to a capability: `Lt2HotkeyListener` resolves the action first and
 * then runs every pre-existing gate unchanged.
 *
 * ### Reserved keys are enumerated, not guessed
 *
 * Measured against the codebase, not assumed (#725 sweep). Most of the list is
 * what the app ITSELF binds:
 *
 *   Enter      activation, 17 call sites
 *   Escape     app-wide dismissal (`useDismissibleLayer`) + LT2 focus clear,
 *              fixed by #648's contract and NOT remappable
 *   ArrowUp/Down  EditOrderPriceModal, EditPositionModal,
 *              DecreaseOrderQuantityEditor, GameDetailPanel, OrgCsvExportPanels
 *   Backspace  trader-chip removal in OrgCsvExportPanels
 *   p          the page-local game pull on /sniper (`usePullHotkey`) and /live
 *              (LiveGame), bound OUTSIDE this registry. Reserved so no action
 *              can be remapped onto it: with S there, one press would place two
 *              orders AND pull the game (#724 StreamRift fold-in, step 6).
 *
 * Tab is the exception and is listed for a different reason: the app binds it
 * ZERO times: it belongs to the BROWSER's focus navigation, and capturing it
 * would strand anyone who does not use a mouse. Reserved on accessibility
 * grounds, not because something here already listens for it.
 *
 * Anything the trader cannot press alone (a bare modifier) or that the browser
 * owns (function keys) is refused by `keyRefusalReason` rather than listed.
 *
 * ### Sniper v2 Phase 2 — the cursor keys, and the two key calls it forced
 *
 * `docs/260909SniperV2Design.md` §8 proposed ARROWS for the level cursor and
 * left `L` doing two jobs. Both are resolved here, and the resolutions are
 * part of the contract rather than a comment in a ticket:
 *
 *   1. **The cursor is J / K / H / L, not the arrows.** §11 asked whether the
 *      cockpit route should take a scoped exception to the app-wide arrow
 *      reservation. DECIDED (Josh, 2026-09-09): it does not. The arrows stay in
 *      `RESERVED_KEYS` for every route, so nothing about "which keys are safe
 *      to bind" depends on where the trader is standing. J/K move the level
 *      cursor down/up, H/L move it between books.
 *
 *   2. **`L` is the book cursor; the latency dump moved to `Y`.** §6 also gave
 *      `L` to "dump the last 50 latency entries to the clipboard". One key
 *      cannot be both, and the cursor keys are pressed hundreds of times an
 *      hour while the dump is pressed when someone is writing up a speed-demon
 *      run — so the cursor keeps the vim-shaped pair and the dump takes `Y`
 *      (yank). `latency-dump` is LIVE since the Phase 3/4 wiring: it calls the
 *      HUD's own `dumpLatencyCsv` and places nothing.
 *
 * Every Phase 2 key is checked against this file's own rules: none of them is
 * in `RESERVED_KEYS`, and `liveHotkeyRegistry.test.ts` proves no two actions
 * ship with the same default.
 *
 * ### Shift-modified bindings
 *
 * A binding token MAY carry a `shift+` prefix (`'shift+f'`). Shift is the only
 * modifier that can appear: Ctrl / Alt / Meta chords belong to the browser and
 * the OS, and `Lt2HotkeyListener` still refuses them outright. A chord is a
 * distinct token, so `f` and `shift+f` are two different keys that two actions
 * can hold without conflicting — which is exactly what §5's composer table
 * asks for (`F` overtake, `Shift+F` release-overtake). Chords are DEFAULTS
 * only: the Settings capture refuses a modifier-carrying keystroke, so a
 * trader's remap is always a bare key. `allowsShift` is the allowlist the
 * listener enforces — an action without it can never fire with Shift held,
 * even if a hand-edited mapping gave it a chord.
 */
import type { Lt2InputMethod } from './liveIntent';

/** What the action DOES. Stable across any remap; safe to persist. */
export type LiveHotkeyActionId =
  | 'passive-top'
  | 'passive-bottom'
  | 'attack-top'
  | 'attack-bottom'
  | 'jump-top'
  | 'jump-bottom'
  | 'pull-all'
  | 'two-sided'
  // Sniper v2 Phase 2 — the level cursor and the band's view keys. None of
  // these places an order; each has `navigation` set and no slot/stamp.
  | 'cursor-down'
  | 'cursor-up'
  | 'book-left'
  | 'book-right'
  | 'cursor-level-1'
  | 'cursor-level-2'
  | 'cursor-level-3'
  | 'cursor-level-4'
  | 'cursor-level-5'
  | 'cursor-level-6'
  | 'cursor-level-7'
  | 'cursor-level-8'
  | 'cursor-level-9'
  | 'cursor-level-top'
  | 'cycle-market'
  | 'dock-toggle'
  // Sniper v2 Phase 3 — the sequence composer (design §5) and Phase 4's
  // latency dump (§6). All DORMANT: they own no key and fire nothing until
  // the relay ops / the HUD land.
  | 'overtake'
  | 'release-overtake'
  | 'overtake-through'
  | 'overtake-both'
  | 'latency-dump';

/**
 * The visibility slot the fence proves a displayed price against: 'a' is the
 * top team / left column, 'd' is the bottom team / right column. Attack shares
 * its passive sibling's slot because it fires on the same book and side.
 * `null` for actions that are not a single-sided order (Pull All).
 */
export type LiveHotkeySlot = 'a' | 'd';

/**
 * What a NAVIGATION action does. Present exactly on the actions that move the
 * level cursor or the band's view state, and absent on every action that can
 * place, replace or cancel an order — so "does this keystroke touch the venue?"
 * is one property read, not a name match.
 *
 * The listener applies these BEFORE the order path's slot/price/visibility
 * fences, because none of them needs a price: they change where the next order
 * key would aim, and that aim is then proved from scratch when a key fires.
 */
export type LiveHotkeyNavigation =
  /** Relative level move (J / K). +1 is one rung further from top of book. */
  | { kind: 'cursor-move'; delta: number }
  /** Absolute level (1–9 / 0). 0 is top of book. */
  | { kind: 'cursor-level'; level: number }
  /** Move the cursor to the other book (H / L). */
  | { kind: 'book-move'; delta: number }
  /** Cycle the lines tab strip ML · SPR · TOT (Z). */
  | { kind: 'cycle-market' }
  /** Standard-viewport dock: context ⇄ control (`` ` ``). */
  | { kind: 'toggle-dock' };

export interface LiveHotkeyMeta {
  id: LiveHotkeyActionId;
  /** The factory key. Every existing trader keeps these (#725: "Current
   *  A/D/W/E/Space behavior remains the default for every existing user"). */
  defaultKey: string;
  /** Settings row label — the trader-facing name of the action. */
  label: string;
  /** One line of what the action does, for the Settings hint. */
  description: string;
  /** Which displayed side the fence checks. Null for non-order actions. */
  slot: LiveHotkeySlot | null;
  /** The durable audit stamp. Null for actions that place no order. */
  inputMethod: Lt2InputMethod | null;
  /**
   * False while the action has a reserved id and default key but no wired
   * behaviour. #724 owns `two-sided`; until it lands it is DORMANT.
   */
  implemented: boolean;
  /**
   * The manager grant this action needs before it is usable, or null when it
   * needs none. An action whose grant the trader does not hold is INACTIVE and
   * owns nothing — see `isActionActive`.
   */
  requiresGrant: LiveHotkeyGrantId | null;
  /**
   * Phase 2 — what this action does to the level cursor / band view. Absent on
   * every order action. An action with `navigation` set must have `slot` and
   * `inputMethod` null: it is not an order and never reaches the intent
   * builders (pinned in `liveHotkeyRegistry.test.ts`).
   */
  navigation?: LiveHotkeyNavigation;
  /**
   * May this action fire while SHIFT is held? Only the two composer entries
   * whose default is a `shift+` chord say yes. The listener treats a missing or
   * false value as a refusal, so a hand-edited mapping that puts a chord on an
   * ordinary action still cannot fire it with a modifier down.
   */
  allowsShift?: boolean;
}

/**
 * The capabilities that decide whether an action is usable at all.
 *
 * `attack` is a MANAGER grant (traderRules.attackAccess). `twoSided` is the
 * trader's OWN opt-in (#724, Settings "post both sides"): S broadens no grant,
 * because each of its two legs is an ordinary passive LT2 order the trader
 * could already place with A and D, so no manager rule stands behind it. It
 * rides the same table as a grant for one reason — the registry's active/
 * inactive rule ("an action the trader cannot use neither fires nor reserves
 * its key") must hold for an un-opted trader exactly as it does for an
 * ungranted one: their S stays ordinary typing and is theirs to rebind.
 */
export type LiveHotkeyGrantId = 'attack' | 'twoSided';

/**
 * The trader's live-trading capabilities, as the registry needs to see them.
 *
 * Deliberately a plain value rather than a store read: the registry is pure so
 * the listener, the Settings panel and the tests all reason about one set of
 * bindings, and a caller cannot accidentally consult a different source.
 */
export interface LiveHotkeyGrants {
  attack: boolean;
  /** The #724 opt-in (displaySettings.lt2TwoSidedEnabled). Off by default. */
  twoSided: boolean;
}

/** Everything ungranted, for callers with no trader in hand (logged out). */
export const NO_LIVE_HOTKEY_GRANTS: LiveHotkeyGrants = { attack: false, twoSided: false };

/**
 * Is this action usable by this trader right now?
 *
 * #725's contract turns on this word. "No two ACTIVE actions may share a key",
 * and "disabled or ungranted actions do not consume their mapped key" — so an
 * action that is dormant (`implemented: false`) or whose grant the trader does
 * not hold neither fires NOR reserves. Both halves matter: reserving without
 * firing is what made a trader without the attack grant unable to reuse W or E
 * for anything, for a capability their account cannot perform.
 */
export function isActionActive(
  id: LiveHotkeyActionId,
  grants: LiveHotkeyGrants,
): boolean {
  const meta = LIVE_HOTKEY_REGISTRY[id];
  if (!meta.implemented) return false;
  return meta.requiresGrant == null || grants[meta.requiresGrant];
}

/** The digit ids, in the order the Settings panel lists them. */
export const CURSOR_LEVEL_DIGIT_IDS = [
  'cursor-level-1',
  'cursor-level-2',
  'cursor-level-3',
  'cursor-level-4',
  'cursor-level-5',
  'cursor-level-6',
  'cursor-level-7',
  'cursor-level-8',
  'cursor-level-9',
] as const;

type CursorLevelDigitId = (typeof CURSOR_LEVEL_DIGIT_IDS)[number];

/**
 * `1`–`9` jump the cursor N rungs off top of book (design §4.1), so the digit
 * IS the level and the two can never drift apart. Written as a generator
 * rather than nine hand-copied blocks for the same reason the alert registry
 * is: nine near-identical literals is nine chances to typo a level into the
 * wrong key, and a mis-levelled cursor aims a live order key at the wrong rung.
 */
function cursorLevelEntries(): Record<CursorLevelDigitId, LiveHotkeyMeta> {
  const out = {} as Record<CursorLevelDigitId, LiveHotkeyMeta>;
  for (const id of CURSOR_LEVEL_DIGIT_IDS) {
    const level = Number(id.slice('cursor-level-'.length));
    out[id] = {
      id,
      defaultKey: String(level),
      label: `Cursor to level ${level}`,
      description: `Jumps the level cursor ${level} rung${level === 1 ? '' : 's'} off top of book on the focused card.`,
      slot: null,
      inputMethod: null,
      requiresGrant: null,
      implemented: true,
      navigation: { kind: 'cursor-level', level },
    };
  }
  return out;
}

export const LIVE_HOTKEY_REGISTRY: Record<LiveHotkeyActionId, LiveHotkeyMeta> = {
  'passive-top': {
    id: 'passive-top',
    defaultKey: 'a',
    label: 'Post top / left side',
    description:
      'Passive post on the focused card — the top team on moneyline, the left column on a two-sided ladder.',
    slot: 'a',
    inputMethod: 'hotkey-a',
    requiresGrant: null,
    implemented: true,
  },
  'passive-bottom': {
    id: 'passive-bottom',
    defaultKey: 'd',
    label: 'Post bottom / right side',
    description:
      'Passive post on the focused card — the bottom team on moneyline, the right column on a two-sided ladder.',
    slot: 'd',
    inputMethod: 'hotkey-d',
    requiresGrant: null,
    implemented: true,
  },
  'attack-top': {
    id: 'attack-top',
    defaultKey: 'w',
    label: 'Attack top / left side',
    description:
      'Bounded aggressive order one level above the displayed bid on the same side as the top / left post. May intentionally take liquidity.',
    slot: 'a',
    inputMethod: 'hotkey-w',
    requiresGrant: 'attack',
    implemented: true,
  },
  'attack-bottom': {
    id: 'attack-bottom',
    defaultKey: 'e',
    label: 'Attack bottom / right side',
    description:
      'Bounded aggressive order one level above the displayed bid on the same side as the bottom / right post. May intentionally take liquidity.',
    slot: 'd',
    inputMethod: 'hotkey-e',
    requiresGrant: 'attack',
    implemented: true,
  },
  // Speed-demon D1 — the JUMP keys. Q/R re-price the trader's ONE resting
  // order on the same slot A/D post on to the bid that slot currently
  // displays, through POST /orders/:id/replace (one relay round trip; amend
  // where the venue allows it, cancel+place otherwise). Same slot, same fence,
  // same audit stamp discipline as A/D — the input method names the action.
  // Refuses truthfully when the card resolves no resting order, more than one
  // candidate, or the order already sits at the displayed price.
  'jump-top': {
    id: 'jump-top',
    defaultKey: 'q',
    label: 'Jump top / left resting order',
    description:
      'Re-prices your one resting order on the top / left side to the bid that side currently displays. One resting order only; pull first when there are several.',
    slot: 'a',
    inputMethod: 'hotkey-q',
    requiresGrant: null,
    implemented: true,
  },
  'jump-bottom': {
    id: 'jump-bottom',
    defaultKey: 'r',
    label: 'Jump bottom / right resting order',
    description:
      'Re-prices your one resting order on the bottom / right side to the bid that side currently displays. One resting order only; pull first when there are several.',
    slot: 'd',
    inputMethod: 'hotkey-r',
    requiresGrant: null,
    implemented: true,
  },
  'pull-all': {
    id: 'pull-all',
    defaultKey: ' ',
    label: 'Pull All on the focused game',
    description:
      'Cancels every resting Kalshi order on the focused game. Resting orders only; Polymarket and unrelated games are never touched.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
  },
  'two-sided': {
    id: 'two-sided',
    defaultKey: 's',
    label: 'Post both sides',
    description:
      'Posts the top and bottom passive orders together on the focused card — the joins the top/left and bottom/right posts would place, at the one-click size, both post-only. Needs bid prices (Maker pricing or an expanded ladder). Off until you turn it on above.',
    // Not a single-sided order: the listener branches on this action BY NAME
    // before the slot is resolved, proves BOTH price rows, and submits the two
    // legs as one group. Each leg keeps its own passive stamp (hotkey-a /
    // hotkey-d) — the durable record says what each order IS, and the group
    // stamp on the same record says they were fired together (#724).
    slot: null,
    inputMethod: null,
    requiresGrant: 'twoSided',
    implemented: true,
  },

  // ── Sniper v2 Phase 2 — the level cursor ─────────────────────────────────
  // Navigation, not orders: each one moves WHERE the next A/D/W/E/Q/R press
  // would act, and the order keys then prove that rung from scratch (the
  // resolver's published level AND the row's own `data-lt2-level` mark).
  'cursor-down': {
    id: 'cursor-down',
    defaultKey: 'j',
    label: 'Level cursor down',
    description:
      'Moves the level cursor one rung further from top of book on the focused card. The order keys act at the cursor.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
    navigation: { kind: 'cursor-move', delta: 1 },
  },
  'cursor-up': {
    id: 'cursor-up',
    defaultKey: 'k',
    label: 'Level cursor up',
    description:
      'Moves the level cursor one rung back toward top of book on the focused card.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
    navigation: { kind: 'cursor-move', delta: -1 },
  },
  'book-left': {
    id: 'book-left',
    defaultKey: 'h',
    label: 'Cursor to the left book',
    description:
      'Draws the level cursor on the top / left book, keeping its level. The order keys still post on their own side.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
    navigation: { kind: 'book-move', delta: -1 },
  },
  'book-right': {
    id: 'book-right',
    defaultKey: 'l',
    label: 'Cursor to the right book',
    description:
      'Draws the level cursor on the bottom / right book, keeping its level. The order keys still post on their own side.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
    navigation: { kind: 'book-move', delta: 1 },
  },
  ...cursorLevelEntries(),
  'cursor-level-top': {
    id: 'cursor-level-top',
    defaultKey: '0',
    label: 'Cursor to top of book',
    description: 'Returns the level cursor to top of book on the focused card.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
    navigation: { kind: 'cursor-level', level: 0 },
  },
  'cycle-market': {
    id: 'cycle-market',
    defaultKey: 'z',
    label: 'Cycle market: ML / SPR / TOT',
    description:
      'Cycles the execution band between the moneyline, spread and total boards.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
    navigation: { kind: 'cycle-market' },
  },
  'dock-toggle': {
    id: 'dock-toggle',
    defaultKey: '`',
    label: 'Toggle dock: context / control',
    description:
      'Flips the standard-viewport dock between the context page and the control page.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
    navigation: { kind: 'toggle-dock' },
  },

  // ── Sniper v2 Phase 3 / 4 — the composer keys and the latency dump ───────
  // The composer's ops are RELAY ops (`POST /api/kalshi/order-sequence`, design
  // §5), so these entries carry the KEYS and nothing else: no slot, no
  // `Lt2InputMethod` — a sequence is audited per leg by the relay, not by a
  // single-order stamp, and `Lt2HotkeyListener` branches on them by name before
  // the slot is resolved.
  //
  // LIVE since the Phase 3 wiring (260909): the key FIRES WHAT THE COMPOSER HAS
  // ARMED and nothing else. Arming — picking the op, its depth and its size —
  // stays a composer concern (`stores/sequenceComposerStore`), so a key can
  // never select an op on the trader's behalf, and a key whose op is not the
  // armed one refuses truthfully rather than firing a plan with a different
  // name (and, for both-sides, twice the size) than the key it was pressed on.
  overtake: {
    id: 'overtake',
    defaultKey: 'f',
    label: 'Overtake at cursor',
    description:
      'Fires the composer’s armed Overtake: takes the cursor level and posts the paired blocker. Arm it on the sequence composer first.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
  },
  'release-overtake': {
    id: 'release-overtake',
    defaultKey: 'shift+f',
    label: 'Release-overtake at cursor',
    description:
      'Fires the composer’s armed Release + overtake: cancels your blocker, takes the cursor level, then re-posts the blocker.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
    allowsShift: true,
  },
  'overtake-through': {
    id: 'overtake-through',
    defaultKey: 'g',
    label: 'Overtake through cursor depth',
    description:
      'Fires the composer’s armed Overtake through: takes every level from top of book down to the cursor, then posts one blocker against the worst take.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
  },
  'overtake-both': {
    id: 'overtake-both',
    defaultKey: 'shift+g',
    label: 'Overtake both books',
    description:
      'Fires the composer’s armed Overtake both sides: the overtake plan mirrored onto the other book of the same event, as one sequence.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
    allowsShift: true,
  },
  'latency-dump': {
    id: 'latency-dump',
    defaultKey: 'y',
    label: 'Dump latency ring',
    description:
      'Copies the last 50 measured key→ack cycles to the clipboard as CSV. Places nothing — a diagnostic yank for the speed-demon lane.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
  },
};

/** Registry order = Settings row order. Stable so the panel does not reshuffle. */
export const LIVE_HOTKEY_ACTION_IDS: readonly LiveHotkeyActionId[] = [
  'passive-top',
  'passive-bottom',
  'attack-top',
  'attack-bottom',
  'jump-top',
  'jump-bottom',
  'pull-all',
  'two-sided',
  // Phase 2 navigation, then Phase 3/4's dormant entries — order is the
  // Settings row order, and the order `sanitizeHotkeyMap` breaks ties in, so
  // the ORDER ACTIONS STAY FIRST: a stored duplicate must cost a trader their
  // cursor key, never their post key.
  'cursor-down',
  'cursor-up',
  'book-left',
  'book-right',
  ...CURSOR_LEVEL_DIGIT_IDS,
  'cursor-level-top',
  'cycle-market',
  'dock-toggle',
  // Phase 3/4: live since the composer wiring landed. Still listed AFTER the
  // order actions — a stored duplicate must cost a trader a sequence key, never
  // their post key.
  'overtake',
  'release-overtake',
  'overtake-through',
  'overtake-both',
  'latency-dump',
] as const;

/** A trader's chosen keys, by action. Partial: an absent id means "default". */
export type LiveHotkeyMap = Partial<Record<LiveHotkeyActionId, string>>;

/**
 * Canonical form of a pressed key, so a stored mapping and a live keystroke
 * compare as equals.
 *
 * Letters fold to lowercase — the listener already did this via
 * `e.key.toLowerCase()`, and the fold is what makes a Shift-chord reach the
 * modifier guard as the same key rather than slipping past as 'A' (#609).
 * Both spellings of the space bar collapse to ' ': older engines report
 * 'Spacebar' where current ones report ' '.
 */
export const HOTKEY_SHIFT_PREFIX = 'shift+';

export function normalizeHotkey(rawKey: string): string {
  if (rawKey === 'Spacebar') return ' ';
  // A Shift chord normalizes prefix-first, so 'Shift+F' and 'shift+f' are the
  // same token and the prefix can never be mistaken for a 6-character key.
  if (rawKey.toLowerCase().startsWith(HOTKEY_SHIFT_PREFIX)) {
    return HOTKEY_SHIFT_PREFIX + normalizeHotkey(rawKey.slice(HOTKEY_SHIFT_PREFIX.length));
  }
  return rawKey.length === 1 ? rawKey.toLowerCase() : rawKey;
}

/**
 * The binding token a keystroke presents, Shift included.
 *
 * The listener resolves the action from THIS, not from the bare key, so `f` and
 * `Shift+F` route to two different actions (design §5's composer pair) while
 * every other modifier stays refused in the listener. Ctrl / Alt / Meta are
 * deliberately absent: a token for them would imply they are bindable.
 */
export function hotkeyTokenFor(rawKey: string, shiftHeld: boolean): string {
  const key = normalizeHotkey(rawKey);
  return shiftHeld ? `${HOTKEY_SHIFT_PREFIX}${key}` : key;
}

/** The bare key inside a token — the token itself when it carries no chord. */
export function hotkeyBaseKey(token: string): string {
  return token.startsWith(HOTKEY_SHIFT_PREFIX)
    ? token.slice(HOTKEY_SHIFT_PREFIX.length)
    : token;
}

/** Keys the app already binds elsewhere. Measured, not assumed — see header. */
export const RESERVED_KEYS: readonly string[] = [
  'Enter',
  'Escape',
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Backspace',
  // Page-local game pull on /sniper and /live — see the header.
  'p',
] as const;

/**
 * How a key reads in the UI. Space has no glyph, so it gets a word —
 * "Spacebar", not "Space", because that is what the shipped copy and
 * `docs/live-trading-v3-attack-pull-baseline.md` already call it. Every
 * teaching string now renders through here, so a shorter word would have
 * quietly retitled the shipped Settings row from "Spacebar Pull All" to
 * "Space Pull All" — a copy change nobody asked for.
 */
export function hotkeyDisplayLabel(key: string): string {
  if (key === ' ') return 'Spacebar';
  if (key.startsWith(HOTKEY_SHIFT_PREFIX)) {
    return `Shift+${hotkeyDisplayLabel(hotkeyBaseKey(key))}`;
  }
  return key.length === 1 ? key.toUpperCase() : key;
}

/** Bare modifiers, lowercased — `normalizeHotkey` leaves named keys cased. */
const MODIFIER_KEYS = new Set(['shift', 'control', 'alt', 'meta', 'capslock', 'os']);

/**
 * Why this key may not be bound, or null when it is acceptable.
 *
 * Fail-closed and SPECIFIC: the reason doubles as the instruction for picking
 * a workable key, the same discipline the hotkey refusal acks follow.
 * Conflict detection is deliberately NOT here — it needs the whole map, and
 * lives in `conflictingAction` below.
 */
export function keyRefusalReason(rawKey: string): string | null {
  // A Shift chord is judged on its BASE key. Chords are shipped defaults, not
  // things a trader can capture (the panel refuses a modifier-carrying
  // keystroke), but a default must still be a token this function accepts —
  // `liveHotkeyRegistry.test.ts` holds the registry to exactly that.
  const key = hotkeyBaseKey(normalizeHotkey(rawKey));
  if (key === '') return 'That key cannot be captured — try another.';
  if (MODIFIER_KEYS.has(key.toLowerCase())) {
    return 'A modifier on its own is not a key — press the key you want to use.';
  }
  if (key === 'p') {
    return 'P pulls the game on the Sniper and Live screens — pick another key.';
  }
  if (RESERVED_KEYS.includes(key)) {
    // Accurate for the whole list: Tab is the browser's, the rest are the
    // app's. "The app already uses it" would be false for Tab, and this
    // string is read by a trader.
    return `${hotkeyDisplayLabel(key)} is reserved for moving around and editing orders.`;
  }
  if (/^F\d{1,2}$/.test(key)) {
    return `${key} is a browser key — pick a letter, number, or punctuation key.`;
  }
  // Anything the engine reports as a multi-character name that we have not
  // explicitly allowed is a named/system key (Home, PageDown, Dead, Unidentified,
  // media keys). Refusing the unknown beats binding a key the trader cannot
  // reliably press.
  if (key.length > 1 && key !== ' ') {
    return `${key} is a system key — pick a letter, number, or punctuation key.`;
  }
  return null;
}

/**
 * THE authority on which action owns which key right now.
 *
 * Everything that needs to know "who has W" reads this — the listener, the
 * Settings panel, the conflict check — so there is one answer rather than
 * three that can drift. Inactive actions are absent entirely: they neither
 * fire nor reserve (#725: "disabled or ungranted actions do not consume their
 * mapped key").
 *
 * Two passes, and the order between them IS the safety rule:
 *
 *   1. A key the trader explicitly chose is claimed first.
 *   2. Factory defaults fill in only what is still free.
 *
 * A stored empty string is the explicit UNBOUND marker and never falls through
 * to a default. Nothing writes it today — before this round `sanitizeHotkeyMap`
 * did, to record who lost a collision — but a payload written by an earlier
 * build or edited by hand can still carry one, and honouring it is what stops
 * that action silently reclaiming a key someone else now holds.
 *
 * That is what makes a grant landing safe. Suppose a trader without the attack
 * grant puts Pull All on W — legal, because W belonged to nothing they could
 * use. The day a manager grants Attack, `attack-top` wants its default W back.
 * It does NOT get it: the trader's deliberate choice outranks a factory
 * default, so Attack arrives UNBOUND and its Settings row asks for a key.
 *
 * The alternative — letting the default win — would silently repoint a key the
 * trader had assigned to "cancel my resting orders" at an action that FIRES AN
 * AGGRESSIVE ORDER, with no interaction to mark the change. On a live trading
 * surface those two outcomes are not close, so the tie is broken toward the
 * trader's stated intent every time.
 *
 * Memoized on the identity of the map and the VALUES of the grants (the
 * listener builds a fresh grants object per keydown, so identity there would
 * never hit). The key listener runs this on every keydown in the app before
 * it can tell an unbound key from a bound one, so the Map + Set + filter it
 * used to allocate per press are now built once per (mapping, grants) change.
 * The stores hand back a stable map (`selectOwnedHotkeys` returns the state
 * object or a shared constant), so a hit is the steady state.
 *
 * A hit is still VERIFIED against the map's values (one property read per
 * action,
 * no allocation) — a caller that edits one map object in place, as the
 * Settings replay in test-utils/lt2DensityWorstCaseKeys does, must see its
 * edit, and identity alone would hand it the table from before the edit.
 */
export function activeHotkeyBindings(
  map: LiveHotkeyMap,
  grants: LiveHotkeyGrants,
): ReadonlyMap<LiveHotkeyActionId, string> {
  const grantKey = grantsCacheKey(grants);
  const cacheable = map != null && typeof map === 'object';
  const byGrants = cacheable ? _bindingsCache.get(map) : undefined;
  const hit = byGrants?.get(grantKey);
  if (hit != null && snapshotMatches(hit.snapshot, map)) return hit.bound;
  const bound = computeActiveHotkeyBindings(map, grants);
  if (cacheable) {
    const entry: BindingsCacheEntry = { snapshot: snapshotOf(map), bound };
    if (byGrants != null) byGrants.set(grantKey, entry);
    else _bindingsCache.set(map, new Map([[grantKey, entry]]));
  }
  return bound;
}

interface BindingsCacheEntry {
  /** The map's value per action at compute time, for the in-place check. */
  snapshot: Record<LiveHotkeyActionId, string | undefined>;
  bound: ReadonlyMap<LiveHotkeyActionId, string>;
}

const _bindingsCache = new WeakMap<LiveHotkeyMap, Map<string, BindingsCacheEntry>>();

function snapshotOf(map: LiveHotkeyMap): Record<LiveHotkeyActionId, string | undefined> {
  const out = {} as Record<LiveHotkeyActionId, string | undefined>;
  for (const id of LIVE_HOTKEY_ACTION_IDS) out[id] = map[id];
  return out;
}

function snapshotMatches(
  snapshot: Record<LiveHotkeyActionId, string | undefined>,
  map: LiveHotkeyMap,
): boolean {
  for (const id of LIVE_HOTKEY_ACTION_IDS) {
    if (snapshot[id] !== map[id]) return false;
  }
  return true;
}

/** Every grant field, in a fixed order, so a grant added to
 *  `LiveHotkeyGrants` later is part of the key by construction. */
function grantsCacheKey(grants: LiveHotkeyGrants): string {
  let key = '';
  for (const id of Object.keys(NO_LIVE_HOTKEY_GRANTS).sort() as LiveHotkeyGrantId[]) {
    key += `${id}=${grants[id] ? 1 : 0};`;
  }
  return key;
}

function computeActiveHotkeyBindings(
  map: LiveHotkeyMap,
  grants: LiveHotkeyGrants,
): ReadonlyMap<LiveHotkeyActionId, string> {
  const bound = new Map<LiveHotkeyActionId, string>();
  const taken = new Set<string>();
  const active = LIVE_HOTKEY_ACTION_IDS.filter((id) => isActionActive(id, grants));

  const claim = (id: LiveHotkeyActionId, key: string): void => {
    if (taken.has(key)) return;
    taken.add(key);
    bound.set(id, key);
  };

  for (const id of active) {
    const chosen = map?.[id];
    // '' is the explicit unbound marker and must not fall through to a default.
    if (chosen == null || chosen === '') continue;
    claim(id, normalizeHotkey(chosen));
  }
  for (const id of active) {
    if (bound.has(id) || map?.[id] === '') continue;
    claim(id, normalizeHotkey(LIVE_HOTKEY_REGISTRY[id].defaultKey));
  }
  return bound;
}

/**
 * Which ACTIVE action already holds `key`, or null when it is free to claim.
 *
 * Grant-aware on purpose. Before #725's second review this walked every action
 * in the registry, so a dormant action (`two-sided` holding S) and an action
 * the trader had no grant for (Attack holding W and E) each blocked a key the
 * trader could never fire. That is the contract mismatch: uniqueness is scoped
 * to ACTIVE actions, and those keys are the trader's to reuse.
 */
export function conflictingAction(
  map: LiveHotkeyMap,
  key: string,
  exceptFor: LiveHotkeyActionId,
  grants: LiveHotkeyGrants,
): LiveHotkeyActionId | null {
  const wanted = normalizeHotkey(key);
  for (const [id, bound] of activeHotkeyBindings(map, grants)) {
    if (id !== exceptFor && bound === wanted) return id;
  }
  return null;
}

/**
 * Reverse lookup for the listener: which action a keystroke fires, or null.
 *
 * Fail-closed, and now for both reasons: an unmapped key is ordinary typing,
 * exactly as it was before the hotkey layer existed, and an action the trader
 * cannot use is not in the binding table at all — so its default key types
 * normally rather than being swallowed by a capability they lack.
 */
export function actionForKey(
  map: LiveHotkeyMap,
  rawKey: string,
  grants: LiveHotkeyGrants,
): LiveHotkeyActionId | null {
  const key = normalizeHotkey(rawKey);
  for (const [id, bound] of activeHotkeyBindings(map, grants)) {
    if (bound === key) return id;
  }
  return null;
}

/**
 * Drop stored entries that are unusable, so one bad value cannot poison the
 * whole mapping (cf. `savedGames.load`, `displaySettings` field validation).
 *
 * STRUCTURAL validity only. This layer deliberately does NOT decide who wins a
 * collision between a stored key and another action's DEFAULT, because that
 * answer depends on the trader's grants and storage does not know them — it
 * used to, and that is what silently discarded a legal binding: a trader with
 * no attack grant putting Pull All on W had the write dropped on its way to
 * disk while the panel said it had been saved.
 *
 * Effective ownership is resolved once, at read time, by
 * `activeHotkeyBindings`. Keeping intent and resolution apart also means a
 * grant being revoked and restored cannot rewrite what the trader chose.
 */
export function sanitizeHotkeyMap(raw: unknown): LiveHotkeyMap {
  if (!raw || typeof raw !== 'object') return {};
  const source = raw as Record<string, unknown>;
  const out: LiveHotkeyMap = {};
  const takenExplicit = new Set<string>();

  for (const id of LIVE_HOTKEY_ACTION_IDS) {
    const stored = source[id];
    // Absent: no stored intent. Left absent so it resolves to the default at
    // read time, where grants are known.
    if (typeof stored !== 'string') continue;

    const candidate = normalizeHotkey(stored);
    if (candidate === '') {
      out[id] = ''; // deliberately unbound — must not fall through to a default
      continue;
    }
    // Structurally unusable, or a key an earlier action already stored: drop
    // the entry so this action falls back to its default. Registry order
    // decides, so hydration is deterministic.
    if (keyRefusalReason(candidate) != null) continue;
    if (takenExplicit.has(candidate)) continue;

    takenExplicit.add(candidate);
    if (candidate !== LIVE_HOTKEY_REGISTRY[id].defaultKey) out[id] = candidate;
  }
  return out;
}

/**
 * Is this an action the trader HOLDS, that no key reaches?
 *
 * Deliberately NOT the same as "has no key". An ungranted or dormant action is
 * SUPPOSED to be keyless — it owns nothing and fires nothing — so it is never
 * unbound here, and a surface built on this answer cannot end up advertising a
 * capability the trader does not have (#740).
 *
 * The cause reachable through the UI is `activeHotkeyBindings`' grant-arrival
 * rule working as designed: the trader had already assigned this action's
 * factory key to something else, so the action arrives with no key rather than
 * silently stealing it back. A stored '' marker or a hand-edited map starves an
 * action the same way — see `activeHotkeyBindings` and `sanitizeHotkeyMap` —
 * and every one of them reads identically here, which is why callers ask about
 * the STATE and not about how it came about.
 *
 * Callers ask this instead of testing `hotkeyLabelFor(...) === '—'`. The em
 * dash is what the state LOOKS like, not what it is; retyping that glyph must
 * never be able to change what a control says about a live order path.
 */
export function isActionUnbound(
  map: LiveHotkeyMap,
  id: LiveHotkeyActionId,
  grants: LiveHotkeyGrants,
): boolean {
  return isActionActive(id, grants) && !activeHotkeyBindings(map, grants).has(id);
}

/**
 * The label a TEACHING surface shows for an action — the trader's own key.
 *
 * Every "press A to…" string in the app resolves through here, so a remap can
 * never leave a hint naming a key that does nothing. An unbound action shows
 * an em dash rather than a key it does not have — and since #725's second
 * review that includes an action which lost its default to a key the trader
 * deliberately assigned elsewhere, or whose grant they do not hold. Naming a
 * key that now fires a DIFFERENT action is the same defect as naming one that
 * fires nothing.
 */
export function hotkeyLabelFor(
  map: LiveHotkeyMap,
  id: LiveHotkeyActionId,
  grants: LiveHotkeyGrants,
): string {
  const bound = activeHotkeyBindings(map, grants).get(id);
  return bound == null ? '—' : hotkeyDisplayLabel(bound);
}

/**
 * The paired form the A/D and W/E surfaces read as, e.g. "A/D".
 *
 * Kept here rather than built at each call site because the separator is part
 * of the product's vocabulary — the focus chip, the header readout, and the
 * Settings row all say the same thing, and the capture harness matches the
 * chip's text exactly.
 */
export function hotkeyPairLabel(
  map: LiveHotkeyMap,
  first: LiveHotkeyActionId,
  second: LiveHotkeyActionId,
  grants: LiveHotkeyGrants,
): string {
  return `${hotkeyLabelFor(map, first, grants)}/${hotkeyLabelFor(map, second, grants)}`;
}
